import { useCallback, useState } from "react";

import { validateDateRange } from "shared/lib/date/validateDateRange";
import { alertDialog } from "shared/ui/feedback";

/**
 * 게시 기간(시작일~종료일) 공용 상태/규칙 훅.
 *
 * 등록·수정 폼에서 "게시 기간을 사용할지" 토글하고, 사용 시 시작일→종료일을
 * 모두 채워야 제출이 가능하도록 하는 규칙을 한 곳에 모은 것.
 *
 * - enabled === false: 기간 미사용(상시). start/end는 null로 비워둔다.
 * - enabled === true : 기간 사용. 시작일을 고른 뒤에야 종료일을 고를 수 있고,
 *                      둘 다 채워져야 isComplete=true.
 *
 * @param {object}  opts
 * @param {string?} opts.initialStart - 초기 시작일(YYYY-MM-DD). 있으면 토글 ON으로 시작.
 * @param {string?} opts.initialEnd   - 초기 종료일(YYYY-MM-DD).
 */
export function usePostingPeriod({ initialStart, initialEnd } = {}) {
  const [enabled, setEnabledState] = useState(
    Boolean(initialStart || initialEnd),
  );
  const [start, setStart] = useState(initialStart || null);
  const [end, setEnd] = useState(initialEnd || null);

  const handleStartChange = (event) => {
    const nextStart = event.target.value;
    const validation = validateDateRange(nextStart, end);

    if (!validation.isValid) {
      alertDialog(validation.message);
      setStart(nextStart);
      setEnd("");
      return;
    }

    setStart(nextStart);
  };

  const handleEndChange = (event) => {
    const nextEnd = event.target.value;
    const validation = validateDateRange(start, nextEnd);

    if (!validation.isValid) {
      alertDialog(validation.message);
      setEnd("");
      return;
    }

    setEnd(nextEnd);
  };

  // 토글 ON/OFF. OFF로 끄면 입력값을 비워 "기간 미사용" 상태로 되돌린다.
  const setEnabled = (nextEnabled) => {
    setEnabledState(nextEnabled);
    if (!nextEnabled) {
      setStart(null);
      setEnd(null);
    }
  };

  // 수정 화면 등에서 서버 값으로 초기화할 때 사용. (useEffect 의존성으로 안전하게 쓰도록 안정 참조)
  const initialize = useCallback((nextStart, nextEnd) => {
    setStart(nextStart || null);
    setEnd(nextEnd || null);
    setEnabledState(Boolean(nextStart || nextEnd));
  }, []);

  // 기간 미사용이면 통과, 사용 시 시작/종료가 모두 채워져야 완료.
  const isComplete = !enabled || Boolean(start && end);

  return {
    enabled,
    setEnabled,
    start,
    end,
    setStart,
    setEnd,
    handleStartChange,
    handleEndChange,
    initialize,
    isComplete,
  };
}

export default usePostingPeriod;
